import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Sun, Cloud, Waves, Wind, Thermometer, Clock, 
  ChevronRight, RefreshCw, Sparkles, Calendar, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripRecommendations({ latitude, longitude, onSelectWindow }) {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (latitude && longitude) {
      fetchRecommendations();
    }
  }, [latitude, longitude]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/weather/recommendations`, {
        params: { latitude, longitude },
        withCredentials: true
      });
      setRecommendations(data);
    } catch (err) {
      setError('Failed to load recommendations');
      console.error('Recommendations fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!latitude || !longitude) return null;

  if (loading) {
    return (
      <Card className="border-2 border-[#E2E8F0]">
        <CardContent className="p-4 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-[#4A5568] animate-spin" />
          <span className="ml-2 text-[#4A5568]">Finding best times...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !recommendations) {
    return (
      <Card className="border-2 border-[#E2E8F0]">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-[#4A5568]">{error || 'No recommendations available'}</p>
          <Button variant="ghost" size="sm" onClick={fetchRecommendations} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { best_window, recommendations: windows, current_score, current_status, total_good_hours_today } = recommendations;

  return (
    <div className="space-y-4">
      {/* Best Time Card */}
      {best_window && (
        <Card className="border-2 border-[#3CB371] bg-gradient-to-br from-[#E8F6ED] to-white overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#3CB371] rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#3CB371] uppercase tracking-wide">Best Time to Boat</p>
                  <p className="font-black text-[#2B3A67] text-lg">{best_window.day}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black text-[#3CB371]">{best_window.score}</p>
                <p className="text-xs font-bold text-[#3CB371]">{best_window.quality}</p>
              </div>
            </div>

            <div className="bg-white/70 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#2B3A67]" />
                <span className="font-bold text-[#2B3A67]">{best_window.time_range}</span>
                <span className="text-sm text-[#4A5568]">({best_window.duration_hours} hours)</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-[#4A5568]">
                <div className="flex items-center gap-1">
                  <Waves className="w-4 h-4" />
                  <span>{best_window.conditions.waves}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Wind className="w-4 h-4" />
                  <span>{best_window.conditions.wind}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Thermometer className="w-4 h-4" />
                  <span>{best_window.conditions.temperature}</span>
                </div>
              </div>
            </div>

            {onSelectWindow && (
              <Button
                onClick={() => onSelectWindow(best_window)}
                className="w-full h-12 bg-[#3CB371] hover:bg-[#2A8A53] text-white font-bold rounded-xl"
              >
                Plan Trip for This Window
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Status */}
      <Card className="border-2 border-[#E2E8F0]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Right Now</p>
              <p className="font-medium text-[#2B3A67]">{current_status}</p>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-black ${current_score >= 70 ? 'text-[#3CB371]' : current_score >= 50 ? 'text-[#E5A000]' : 'text-[#D32F2F]'}`}>
                {current_score}
              </p>
              <p className="text-xs text-[#4A5568]">{total_good_hours_today}h good today</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* More Windows */}
      {windows.length > 1 && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center justify-between w-full text-left mb-3"
          >
            <span className="font-bold text-[#2B3A67]">More Good Windows</span>
            <ChevronRight className={`w-5 h-5 text-[#4A5568] transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>

          {expanded && (
            <div className="space-y-2">
              {windows.slice(1, 5).map((window, idx) => (
                <Card 
                  key={idx} 
                  className={`border-2 ${window.quality_color === 'green' ? 'border-[#3CB371]/30' : 'border-[#E5A000]/30'} hover:border-[#2B3A67] transition-colors cursor-pointer`}
                  onClick={() => onSelectWindow && onSelectWindow(window)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          window.quality_color === 'green' ? 'bg-[#E8F6ED]' : 'bg-[#FDF3E1]'
                        }`}>
                          <Calendar className={`w-4 h-4 ${
                            window.quality_color === 'green' ? 'text-[#3CB371]' : 'text-[#E5A000]'
                          }`} />
                        </div>
                        <div>
                          <p className="font-bold text-[#2B3A67] text-sm">{window.day}</p>
                          <p className="text-xs text-[#4A5568]">{window.time_range}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-black ${
                          window.quality_color === 'green' ? 'text-[#3CB371]' : 'text-[#E5A000]'
                        }`}>
                          {window.score}
                        </p>
                        <p className="text-xs text-[#4A5568]">{window.duration_hours}h</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Refresh */}
      <div className="text-center">
        <Button variant="ghost" size="sm" onClick={fetchRecommendations} className="text-[#4A5568]">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh Recommendations
        </Button>
      </div>
    </div>
  );
}
